import styled from "styled-components/macro"
import { boxShadow, colors } from "../../../../../styles/design.config"
import { useDispatch } from "react-redux"
import { getIdFromParams } from "../../../../../utils/company"
import { useParams } from "react-router-dom"
import { CSSProperties, useEffect, useState } from "react"
import {
  CreateOrEditRuleReset,
  GetTransactionsThunk,
  Transaction,
  getDataToCreateRuleThunk,
} from "../../../../../store/ducks/bank.ducks"
import * as Ct from "ldlj"
import { useRNBSelector } from "../../../../../store/rootReducer"
import { VariableSizeGrid } from "react-window"
import {
  ChevronProps,
  ClickableTitleSort,
  SortToReturn,
  TitleTable,
} from "../../../../../components/Commons/Table"
import React from "react"
import AutoSizer from "react-virtualized-auto-sizer"
import {
  GridWrapper,
  HeaderTitleWrapper,
  HeaderWrapper,
  LoaderWrapper,
  TableWrapper,
} from "../../../../../components/dropDocuments/StyledDropsComponents"
import { Alert } from "../../../../../components/Commons/Alert"
import { useIntl } from "react-intl"
import { Text } from "../../../../../components/Commons/Text"
import { Button } from "../../../../../components/Commons/Button"
import { DisplayTransactionModal } from "../../../../../components/bank/DisplayTransactionModal"
import { DateTime } from "luxon"
import { ReactComponent as Search } from "../../../../../assets/search.svg"
import {
  searchTransactions,
  sortTransactionsByDate,
  sortTransactionsByDescription,
} from "../../../../../utils/bank"

export const Transactions = () => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const accountID = getIdFromParams(useParams())("account_id")
  const accounts = useRNBSelector((state) => state.bank.accounts)
  const currentAcccount = Object.values(accounts).find(
    (a) => a.id === accountID
  )
  const getTransactionsStatus = useRNBSelector(
    (state) => state.bank.getTransactionsStatus
  )
  const createOrEditRuleStatus = useRNBSelector(
    (state) => state.bank.createOrEditRuleStatus
  )
  const userTypology = useRNBSelector((state) => state.user.typology)

  const [rowData, setRowData] = useState<Transaction[]>([])
  const [listWidth, setListWidth] = useState(0)
  const [listHeight, setListHeight] = useState(0)
  const listRef = React.createRef<VariableSizeGrid>()
  const [columnToSort, setColumnToSort] = useState<SortToReturn | null>(null)
  const [currentChevron, setCurrentChevron] = useState<ChevronProps>({
    direction: "none",
    index: 0,
  })
  const [search, setSearch] = useState<string>("")
  const [displayTransaction, setDisplayTransaction] =
    useState<Transaction | null>(null)

  useEffect(() => {
    if (
      Object.values(accounts).length > 0 &&
      currentAcccount &&
      currentAcccount.transactions
    ) {
      setRowData(searchTransactions(search, currentAcccount.transactions))
    }
  }, [Object.values(accounts).length, currentAcccount?.transactions, search])

  useEffect(() => {
    if (accountID && Object.values(accounts).length > 0) {
      dispatch(GetTransactionsThunk(accountID))
    }
  }, [accountID, dispatch, Object.values(accounts).length])

  const firstColumns = [
    {
      headerText: "bank-management.transaction.table-header.date",
      content: (row: Transaction) => (
        <>
          <Ct.Spacer width={2} height={0} />
          <ColumnRow clickable={userTypology !== "customer"}>
            <Row
              text={DateTime.fromJSDate(new Date(row.date)).toFormat(
                "dd/MM/yyyy"
              )}
              notClickable={userTypology === "customer"}
            />
            {userTypology !== "customer" && (
              <>
                {row.second_row_vat && (
                  <Row
                    text={DateTime.fromJSDate(new Date(row.date)).toFormat(
                      "dd/MM/yyyy"
                    )}
                  />
                )}
                <Row
                  text={DateTime.fromJSDate(new Date(row.date)).toFormat(
                    "dd/MM/yyyy"
                  )}
                />
              </>
            )}
          </ColumnRow>
        </>
      ),
    },
    {
      headerText: "bank-management.transaction.table-header.label",
      content: (row: Transaction) => (
        <ColumnRow clickable={userTypology !== "customer"}>
          <Row
            text={row.description}
            notClickable={userTypology === "customer"}
          />
          {userTypology !== "customer" && (
            <>
              {row.second_row_vat && <Row text={row.description} />}
              <Row text={row.description} />
            </>
          )}
        </ColumnRow>
      ),
    },
  ]

  const customerColumns = [
    {
      headerText: "bank-management.transaction.table-header.amount",
      content: (row: Transaction) => (
        <ColumnRow clickable={userTypology !== "customer"}>
          <Row text={row.amount} notClickable={userTypology === "customer"} />
        </ColumnRow>
      ),
    },
  ]

  const accountingColumns = [
    {
      headerText: "bank-management.transaction.table-header.debit",
      content: (row: Transaction) => {
        const empty = intl.formatMessage({
          id: "bank-management.transaction.table-body.empty-space",
        })

        const firstRow = row.first_row?.debit ? row.first_row.debit : empty
        const secondRow =
          row.second_row_vat && row.second_row_vat?.debit
            ? row.second_row_vat.debit
            : empty
        const thirdRow = row.third_row?.debit ? row.third_row?.debit : empty
        return (
          <ColumnRow clickable={userTypology !== "customer"}>
            <Row text={firstRow} />
            {row.second_row_vat && <Row text={secondRow} />}
            <Row text={thirdRow} />
          </ColumnRow>
        )
      },
    },
    {
      headerText: "bank-management.transaction.table-header.credit",
      content: (row: Transaction) => {
        const empty = intl.formatMessage({
          id: "bank-management.transaction.table-body.empty-space",
        })

        const firstRow = row.first_row?.credit ? row.first_row.credit : empty
        const secondRow =
          row.second_row_vat && row.second_row_vat?.credit
            ? row.second_row_vat.credit
            : empty
        const thirdRow = row.third_row?.credit ? row.third_row?.credit : empty
        return (
          <ColumnRow clickable={userTypology !== "customer"}>
            <Row text={firstRow} />
            {row.second_row_vat && <Row text={secondRow} />}
            <Row text={thirdRow} />
          </ColumnRow>
        )
      },
    },
    {
      headerText: "bank-management.transaction.table-header.assignment",
      content: (row: Transaction) => {
        return (
          <ColumnRow clickable={userTypology !== "customer"}>
            <Row text={row.first_row?.assignment} />
            {row.second_row_vat && <Row text={row.second_row_vat.assignment} />}
            <Row text={row.third_row?.assignment} />
          </ColumnRow>
        )
      },
    },
  ]

  const columns =
    userTypology !== "customer"
      ? [...firstColumns, ...accountingColumns]
      : [...firstColumns, ...customerColumns]

  const [hoveredId, setHoveredId] = useState<number>(0)

  const Cell = ({
    columnIndex,
    rowIndex,
    style,
  }: {
    columnIndex: number
    rowIndex: number
    style: CSSProperties | undefined
  }) => {
    const transaction: Transaction = rowData[rowIndex]

    return (
      <div
        style={{
          ...style,
        }}
        key={columnIndex}
        onClick={() => {
          if (userTypology === "customer") {
            return
          }
          setDisplayTransaction(transaction)
          if (accountID) {
            dispatch(getDataToCreateRuleThunk(accountID))
          }
        }}
        onMouseEnter={() => {
          setHoveredId(transaction.id)
        }}
      >
        {rowIndex > 0 && <Ct.Separator size="full" color={"lavender"} />}
        <StyledCell
          style={{ height: "100%", width: "100%" }}
          alignItems={
            columnIndex === 0 || columnIndex === 1 ? "flex-start" : "center"
          }
          backgroundColor={
            transaction.id === hoveredId && userTypology !== "customer"
              ? colors.cornflower
              : rowIndex % 2 === 0
              ? "white"
              : "#F4F8FF"
          }
          color={
            transaction.id === hoveredId && userTypology !== "customer"
              ? colors.white
              : colors.navy
          }
          clickable={userTypology !== "customer"}
        >
          <Ct.Row
            style={{
              width: "100%",
              justifyContent:
                columnIndex === 0 || columnIndex === 1
                  ? "flex-start"
                  : "center",
              height: "100%",
            }}
          >
            {columnIndex === 1 && <Ct.Spacer width={2} height={0} />}

            {columns[columnIndex].content(transaction)}
          </Ct.Row>
        </StyledCell>
      </div>
    )
  }

  enum SortOptionsValues {
    "date",
    "description",
  }

  const [sortType, setSortType] = useState<
    { type: SortOptionsValues; asc: boolean } | undefined
  >({ type: SortOptionsValues.date, asc: true })

  const sorter = (asc: boolean) => (option: SortOptionsValues) => {
    setSortType({ type: option, asc: asc })
    if (option === SortOptionsValues.date) {
      setRowData(sortTransactionsByDate(rowData, asc))
    } else if (option === SortOptionsValues.description) {
      setRowData(sortTransactionsByDescription(rowData, asc))
    }
  }

  useEffect(() => {
    if (columnToSort) {
      const currentSort: SortOptionsValues = Object.values(
        SortOptionsValues
      ).indexOf(SortOptionsValues[columnToSort.index])

      if (columnToSort.direction === "up") {
        setCurrentChevron({ index: columnToSort.index, direction: "up" })
        sorter(columnToSort.asc)(currentSort)
      } else if (columnToSort.direction === "down") {
        setCurrentChevron({ index: columnToSort.index, direction: "down" })
        sorter(columnToSort.asc)(currentSort)
      } else {
        setCurrentChevron({ index: columnToSort.index, direction: "none" })
        if (
          Object.values(accounts).length > 0 &&
          currentAcccount &&
          currentAcccount.transactions
        ) {
          setRowData(currentAcccount.transactions)
        }
      }
    }
  }, [columnToSort])

  return (
    <Content>
      <StyledSection>
        <Ct.Spacer height={3} />

        <StyledInput
          name={"searchRule"}
          id={"searchRule"}
          label="Rechercher"
          value={search}
          suffix={<Search />}
          maxWidth={30}
          onChange={(event: { target: HTMLInputElement }) => {
            const value = event.target.value.trimStart().replace("  ", " ")
            setSearch(value.replace(/[^a-zA-Z0-9 .-]/g, ""))
          }}
          shadowed={true}
          noBorder={true}
        />
        <Ct.Spacer height={3} />

        <StyledTable>
          <AutoSizer>
            {({ height, width }) => {
              const columnWidths = columns.map((_, index) => {
                if (userTypology !== "customer") {
                  if (index === 0) {
                    return (10 / 100) * width
                  }
                  if (index === 1) {
                    return (50 / 100) * width
                  }
                  if (index === 2) {
                    return (10 / 100) * width
                  }
                  if (index === 3) {
                    return (10 / 100) * width
                  }
                  if (index === 4) {
                    return (20 / 100) * width
                  }

                  return (6 / 100) * width
                } else {
                  if (index === 0) {
                    return (15 / 100) * width
                  }
                  if (index === 1) {
                    return (45 / 100) * width
                  }
                  if (index === 2) {
                    return (40 / 100) * width
                  }

                  return (6 / 100) * width
                }
              })

              if (
                listRef?.current &&
                (width !== listWidth || height !== listHeight)
              ) {
                listRef.current.resetAfterColumnIndex(0, true)
              }
              setListWidth(width)
              setListHeight(height)

              const rowHeights = rowData.map((r) =>
                userTypology === "customer" ? 50 : r.second_row_vat ? 110 : 80
              )

              return (
                <>
                  <HeaderWrapper totalWidth={width} paddingHeader={"0 1rem"}>
                    {columns.map((column, index) => (
                      <HeaderTitleWrapper
                        justifyContent={
                          index === 0 || index === 1 ? "flex-start" : "center"
                        }
                        calculatedWidth={columnWidths[index] - 30}
                        key={column.headerText}
                      >
                        {index < 2 ? (
                          <ClickableTitleSort
                            tid={column.headerText}
                            intl={intl}
                            cursor={"pointer"}
                            index={index}
                            sortToReturn={(column: SortToReturn) => {
                              setColumnToSort(column)
                              if (rowData.length > 0 && sortType) {
                                sorter(sortType.asc)(sortType.type)
                              }
                            }}
                            sortableColumnsLength={columns.length - 3}
                            currentChevron={currentChevron}
                          />
                        ) : (
                          <TitleTable tid={column.headerText} intl={intl} />
                        )}
                      </HeaderTitleWrapper>
                    ))}
                  </HeaderWrapper>

                  {rowData.length === 0 ? (
                    <LoaderWrapper totalWidth={width} height={height - 56}>
                      {getTransactionsStatus === "ATTEMPT" ? (
                        <div>
                          <Button label={""} loadingStatus={"loading"} />
                        </div>
                      ) : (
                        <Alert alertType="info">
                          <Text
                            text={intl.formatMessage({
                              id:
                                currentAcccount?.transactions &&
                                currentAcccount?.transactions?.length > 0
                                  ? "bank-management.transaction.table-body.empty-transactions-search"
                                  : "bank-management.transaction.table-body.empty-transactions",
                            })}
                          />
                        </Alert>
                      )}
                    </LoaderWrapper>
                  ) : (
                    <GridWrapper totalWidth={width}>
                      <VariableSizeGrid
                        ref={listRef}
                        height={height - 56}
                        rowCount={rowData.length}
                        width={width}
                        columnWidth={(index) => columnWidths[index]}
                        rowHeight={(index) => rowHeights[index]}
                        columnCount={columns.length}
                      >
                        {Cell}
                      </VariableSizeGrid>
                    </GridWrapper>
                  )}
                </>
              )
            }}
          </AutoSizer>
        </StyledTable>

        {displayTransaction && userTypology !== "customer" && (
          <DisplayTransactionModal
            isDisplayed={!!displayTransaction}
            onClose={() => {
              setDisplayTransaction(null)
              if (createOrEditRuleStatus === "SUCCESS") {
                dispatch(CreateOrEditRuleReset())
                if (accountID && Object.values(accounts).length > 0) {
                  dispatch(GetTransactionsThunk(accountID))
                }
              }
            }}
            transaction={displayTransaction}
          />
        )}
      </StyledSection>
    </Content>
  )
}

const Content = styled.div`
  padding-bottom: 4rem;
  display: flex;
  height: 100%;
  box-sizing: border-box;
`

const StyledSection = styled.section`
  border-radius: 0 2.5rem 2.5rem;
  background-color: ${colors.white};
  box-shadow: ${boxShadow};
  padding: 0 2rem 4rem;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  overflow: hidden;
`

interface StyledCellProps {
  width: string
  alignItems: string
  backgroundColor: string
  color: string
  clickable: boolean
}
const StyledCell = styled(Ct.ColumnCenterCenter)<StyledCellProps>`
  width: ${(props) => props.width};
  align-items: ${(props) => props.alignItems};
  cursor: ${(props) => (props.clickable ? "pointer" : "initial")};
  background-color: ${(props) => props.backgroundColor};
  & span {
    color: ${(props) => props.color};
  }
`
const StyledTable = styled(TableWrapper)`
  border: none;
`

const ColumnRow = styled.div<{ clickable: boolean }>`
  display: flex;
  flex-direction: column;
  cursor: ${(props) => (props.clickable ? "pointer" : "initial")};
`
const Row = styled((props) => <Ct.Text {...props} />)`
  display: flex;
  align-items: center;
  padding-top: 2rem;
`
const StyledInput = styled((props) => <Ct.Input {...props} />)<{
  notClickable?: boolean
}>`
  box-shadow: ${boxShadow};
  cursor: ${(props) => (props.notClickable ? "initial" : "pointer")};
`
